var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

var eps = 1e-6;

var DataProvider =
/** @class */
function () {
  function DataProvider(dimensions) {
    this.samples = [];
    this.scalars = new Set();
    this.axisToIdx = {};
    this.idxToAxis = {};
    this.axes = null;
    this.dimensions = dimensions;
  }

  DataProvider.prototype.setData = function (samples, discardAxes) {
    if (discardAxes === void 0) {
      discardAxes = true;
    }

    this.samples = [];
    this.axes = {};
    this.scalars = new Set();
    this.axisToIdx = {};
    this.idxToAxis = {};

    if (samples.length === 0) {
      return;
    }

    var compositions = {};

    for (var _i = 0, samples_1 = samples; _i < samples_1.length; _i++) {
      var sample = samples_1[_i];
      var elements_4 = Object.keys(sample.composition);

      for (var _a = 0, elements_1 = elements_4; _a < elements_1.length; _a++) {
        var element = elements_1[_a];

        if (!(element in compositions)) {
          compositions[element] = new Set();
        }

        compositions[element].add(sample.composition[element]);
      }

      for (var _b = 0, _c = Object.keys(sample.scalars); _b < _c.length; _b++) {
        var key = _c[_b];
        this.scalars.add(key);
      }
    }

    var elements = Object.keys(compositions);
    this.axes = {};

    var _loop_1 = function _loop_1(element) {
      var values = [];
      compositions[element].forEach(function (val) {
        values.push(val);
      });
      values.sort(function (a, b) {
        return a < b ? -1 : a > b ? 1 : 0;
      });
      var range = [values[0], values[values.length - 1]];
      var spacing = values.length > 1 ? values[1] - values[0] : 0;
      this_1.axes[element] = {
        element: element,
        spacing: spacing,
        range: range
      };
    };

    var this_1 = this;

    for (var _d = 0, elements_2 = elements; _d < elements_2.length; _d++) {
      var element = elements_2[_d];

      _loop_1(element);
    }

    for (var _e = 0, elements_3 = elements; _e < elements_3.length; _e++) {
      var element = elements_3[_e];
      var axis = this.axes[element];

      if (discardAxes && Math.abs(axis.spacing) < eps) {
        delete this.axes[element];
      }
    }

    if (Object.keys(this.axes).length !== this.dimensions) {
      console.warn("A quaternary data provider can only span a " + this.dimensions + "-dimensional space, but " + elements.length + " different elements are found in the dataset"); // throw new Error(`A quaternary data provider can only span a ${this.dimensions}-dimensional space, but ${elements.length} different elements are found in the dataset`);
    }

    this.setAxisOrder(Object.keys(this.axes));
    this.samples = samples;
    this.setActiveScalar(this.getDefaultScalar(this.getActiveScalar()));
  };

  DataProvider.prototype.setAxisOrder = function (order) {
    this.axisToIdx = {};

    for (var i = 0; i < order.length; ++i) {
      var key = order[i];
      this.axisToIdx[key] = i;
      this.idxToAxis[i] = key;
    }
  };

  DataProvider.prototype.slice = function (axes) {
    return DataProvider.filterSamples(this.samples, axes);
  };

  DataProvider.prototype.getDefaultScalar = function (key) {
    if (key === void 0) {
      key = null;
    }

    if (this.scalars.has(key)) {
      return key;
    } else {
      try {
        return this.scalars.values().next().value;
      } catch (e) {
        return null;
      }
    }
  };

  DataProvider.prototype.setAxes = function (axes) {
    // Ensure the new axes match the elements in the dataset
    for (var _i = 0, _a = Object.keys(axes); _i < _a.length; _i++) {
      var key = _a[_i];

      if (!(key in this.axes)) {
        console.warn("Setting axis " + key + ", but no samples in the dataset span this dimension");
      }

      this.axes[key] = axes[key];
    }
  };

  DataProvider.prototype.getAxes = function () {
    return this.axes;
  };

  DataProvider.prototype.getAxis = function (val) {
    var axisKey = null;

    if (typeof val === 'string') {
      if (val in this.axisToIdx) {
        axisKey = val;
      }
    } else {
      if (val in this.idxToAxis) {
        axisKey = this.idxToAxis[val];
      }
    }

    if (axisKey) {
      return this.axes[axisKey];
    } else {
      return null;
    }
  };

  DataProvider.prototype.getAxisLabel = function (val) {
    var axis = this.getAxis(val);
    return axis ? axis.element.charAt(0).toUpperCase() + axis.element.slice(1) : 'X';
  };

  DataProvider.prototype.getScalarRange = function (key) {
    var values = this.samples.map(function (sample) {
      return DataProvider.getSampleScalar(sample, key);
    });
    return [Math.min.apply(Math, values), Math.max.apply(Math, values)];
  };

  DataProvider.prototype.getScalars = function () {
    var scalars = [];
    this.scalars.forEach(function (val) {
      scalars.push(val);
    });
    return scalars;
  };

  DataProvider.prototype.getActiveScalar = function () {
    return this.activeScalar;
  };

  DataProvider.prototype.setActiveScalar = function (key) {
    if (this.scalars.has(key)) {
      this.activeScalar = key;
    } else {
      console.warn("Unable to set " + key + " as active scalar");
    }
  };

  DataProvider.getSampleScalar = function (sample, scalar) {
    if (scalar in sample.scalars) {
      return sample.scalars[scalar];
    } else {
      return null;
    }
  };

  DataProvider.filterSamples = function (samples, axes) {
    var _loop_2 = function _loop_2(axis) {
      if (typeof axis.range === 'function') {
        samples = samples.filter(function (sample) {
          var fraction = axis.element in sample.composition ? sample.composition[axis.element] : 0;
          return axis.range(fraction, eps);
        });
      } else {
        samples = samples.filter(function (sample) {
          var fraction = axis.element in sample.composition ? sample.composition[axis.element] : 0;

          if (axis.range[0] - eps < fraction && fraction < axis.range[1] + eps) {
            return true;
          } else {
            return false;
          }
        });
      }
    };

    for (var _i = 0, axes_1 = axes; _i < axes_1.length; _i++) {
      var axis = axes_1[_i];

      _loop_2(axis);
    }

    return samples;
  };

  DataProvider.isSelected = function (sample, selectedKeys) {
    return selectedKeys.has(sample._id);
  };

  return DataProvider;
}();

export { DataProvider };

var HeatMapDataProvider =
/** @class */
function () {
  function HeatMapDataProvider() {
    this.data = [];
    this.scalars = new Set();
    this.numY = 10;
    this.separateSlope = false;
    this.segments = [];
    this.selection = '';
    this.setReduceFn();
  }

  HeatMapDataProvider.prototype.setData = function (data) {
    if (data === void 0) {
      data = [];
    }

    this.data = data;
    this.scalars = new Set();

    for (var _i = 0, data_1 = data; _i < data_1.length; _i++) {
      var d = data_1[_i];

      for (var _a = 0, _b = d.spectrum.getKeys(); _a < _b.length; _a++) {
        var key = _b[_a];
        this.scalars.add(key);
      }
    }
  };

  HeatMapDataProvider.prototype.setActiveScalars = function (keys) {
    this.activeScalars = [null, null];

    for (var i = 0; i < keys.length; ++i) {
      var key = keys[i];

      if (this.scalars.has(key)) {
        this.activeScalars[i] = key;
      } else {
        console.warn("Unable to set " + key + " as active scalar");
      }
    }
  };

  HeatMapDataProvider.prototype.getActiveScalars = function () {
    return this.activeScalars;
  };

  HeatMapDataProvider.prototype.setNumY = function (n) {
    if (n === void 0) {
      n = 10;
    }

    this.numY = n;
  };

  HeatMapDataProvider.prototype.setSeparateSlope = function (flag) {
    this.separateSlope = flag;
  };

  HeatMapDataProvider.prototype.computeMaps = function () {
    // Values in the Y axis can be repeated
    var minY = Infinity;
    var maxY = -Infinity;

    for (var _i = 0, _a = this.data; _i < _a.length; _i++) {
      var d = _a[_i];

      if (!d.spectrum.hasKey(this.getActiveScalars()[0])) {
        continue;
      }

      minY = Math.min.apply(Math, [minY].concat(d.spectrum.getArray(this.getActiveScalars()[0])));
      maxY = Math.max.apply(Math, [maxY].concat(d.spectrum.getArray(this.getActiveScalars()[0])));
    }

    var slopeFactor = this.separateSlope ? 2 : 1;
    var spacing = (maxY - minY) / this.numY;

    if (this.separateSlope) {
      this.findSegments();
    }

    this.Y = [];

    for (var i = 0; i < this.numY; ++i) {
      if (this.separateSlope) {
        this.Y.push((minY + i * spacing).toFixed(2) + " (+)");
      } else {
        this.Y.push("" + (minY + i * spacing).toFixed(2));
      }
    }

    if (this.separateSlope) {
      for (var i = 0; i < this.numY; ++i) {
        this.Y.push((maxY - (i + 1) * spacing).toFixed(2) + " (-)");
      }
    }

    this.indexMaps = [];

    for (var i = 0; i < this.data.length; ++i) {
      if (!this.data[i].spectrum.hasKey(this.getActiveScalars()[0])) {
        this.indexMaps.push([]);
        continue;
      }

      var yData = this.data[i].spectrum.getArray(this.getActiveScalars()[0]);
      var map = [];

      for (var j = 0; j < this.numY * slopeFactor; ++j) {
        map.push([]);
      }

      for (var j = 0; j < yData.length; ++j) {
        // Only include the Y values in the selected segments
        if (this.inSelectedSegment(j)) {
          var idx = Math.min(Math.round((yData[j] - minY) / spacing), this.numY - 1);

          if (this.separateSlope && j > 0) {
            var slope = yData[j] >= yData[j - 1] ? 1 : -1;

            if (slope === -1) {
              idx = 2 * this.numY - idx - 1;
            }
          }

          map[idx].push(j);
        }
      }

      this.indexMaps.push(map);
    } // Take the median of the Z values occurring at a specific Y value


    this.Z = [];

    for (var i = 0; i < this.indexMaps.length; ++i) {
      var map = this.indexMaps[i];
      var vals = [];

      for (var j = 0; j < map.length; ++j) {
        var y = map[j];
        var v = [];

        for (var k = 0; k < y.length; ++k) {
          var idx = y[k];
          v.push(this.data[i].spectrum.getArray(this.getActiveScalars()[1])[idx]);
        }

        vals.push(this.reduceFn(v));
      }

      this.Z.push(vals);
    } //


    this.X = [];

    for (var _b = 0, _c = this.data; _b < _c.length; _b++) {
      var d = _c[_b];
      this.X.push(Object.entries(d.sample.composition).map(function (_a) {
        var key = _a[0],
            val = _a[1];
        return "" + key.charAt(0).toUpperCase() + key.slice(1) + ": " + val.toFixed(1);
      }).join(', '));
    }
  };

  HeatMapDataProvider.prototype.getSegments = function () {
    return this.segments;
  };

  HeatMapDataProvider.prototype.selectSegments = function (selection) {
    this.selection = selection;
  };

  HeatMapDataProvider.prototype.setReduceFn = function (name) {
    if (name === void 0) {
      name = 'median';
    }

    var reduceFunctions = {
      'min': function min(values) {
        return Math.min.apply(Math, values);
      },
      'max': function max(values) {
        return Math.max.apply(Math, values);
      },
      'mean': function mean(values) {
        var sum = 0;

        for (var _i = 0, values_1 = values; _i < values_1.length; _i++) {
          var v = values_1[_i];
          sum += v;
        }

        return sum / values.length;
      },
      'median': function median(values) {
        values = values.sort(function (a, b) {
          return a < b ? -1 : 1;
        });
        return values[Math.floor(values.length / 2)];
      }
    };
    this.reduceFn = reduceFunctions[name];
  };

  HeatMapDataProvider.prototype.findSegments = function () {
    this.segments = [];

    if (this.data.length == 0) {
      return;
    }

    if (!this.data[0].spectrum.hasKey(this.getActiveScalars()[0])) {
      return;
    }

    var Y = this.data[0].spectrum.getArray(this.getActiveScalars()[0]);
    var currSlope = null;

    for (var i = 1; i < Y.length; ++i) {
      var slope = Y[i] - Y[i - 1] >= 0;

      if (slope !== currSlope) {
        this.segments.push({
          start: i,
          stop: i,
          selected: false
        });
      } else {
        var currSegment = this.segments.pop();
        currSegment = __assign({}, currSegment, {
          stop: i
        });
        this.segments.push(currSegment);
      }

      currSlope = slope;
    }

    this.setActiveSegments(this.selection);
  };

  HeatMapDataProvider.prototype.setActiveSegments = function (selection) {
    var s = selection.split(',');
    var init = selection.trim() == '';

    for (var i = 0; i < this.segments.length; ++i) {
      this.segments[i].selected = init;
    }

    for (var _i = 0, s_1 = s; _i < s_1.length; _i++) {
      var seg = s_1[_i];
      var range = seg.split('-');
      var start = parseInt(range[0]);
      var stop_1 = start;

      if (range.length > 1) {
        stop_1 = parseInt(range[1]);
      }

      if (!isFinite(start) || !isFinite(stop_1)) {
        continue;
      }

      if (stop_1 < start) {
        continue;
      }

      for (var idx = start; idx <= stop_1; ++idx) {
        if (idx < this.segments.length) {
          this.segments[idx].selected = true;
        }
      }
    }
  };

  HeatMapDataProvider.prototype.inSelectedSegment = function (idx) {
    if (!this.separateSlope || this.segments.length === 0) {
      return true;
    } // Will write the function in ln(n) complexity one day...


    for (var _i = 0, _a = this.segments; _i < _a.length; _i++) {
      var segment = _a[_i];

      if (idx >= segment.start && idx < segment.stop && segment.selected) {
        return true;
      }
    }

    return false;
  };

  return HeatMapDataProvider;
}();

export { HeatMapDataProvider };